// src/config.js
// NOTE: CRA only replaces process.env.REACT_APP_* with static access.
// Dynamic access like process.env[key] does NOT get replaced at build time.

// Canonical environments. `local` is for local development/test ONLY and is the
// sole tier allowed to default to localhost. `dev`, `staging`, `prod` are the
// deployable tiers and FAIL CLOSED — every endpoint/Auth0 value must come from a
// REACT_APP_* var, so a deployed build can never silently target a developer's
// machine. (#370)
const SUPPORTED_ENVIRONMENTS = ['local', 'dev', 'staging', 'prod'];

// Common stale names → the canonical one, so a misconfiguration gives an
// actionable error instead of silently falling back to localhost.
const ENVIRONMENT_ALIASES = {
    production: 'prod',
    prd: 'prod',
    development: 'dev',
    develop: 'dev',
    stage: 'staging',
    test: 'local',
};

const resolveEnvironment = () => {
    const raw = process.env.REACT_APP_ENVIRONMENT;
    // Unset is the intentional local-dev/test default.
    if (!raw || raw.trim() === '') {
        return 'local';
    }
    if (SUPPORTED_ENVIRONMENTS.includes(raw)) {
        return raw;
    }
    const suggestion = ENVIRONMENT_ALIASES[raw];
    const hint = suggestion ? ` Did you mean "${suggestion}"?` : '';
    throw new Error(
        `Invalid REACT_APP_ENVIRONMENT "${raw}". Supported environments are: ` +
        `${SUPPORTED_ENVIRONMENTS.join(', ')}.${hint}`
    );
};

const ENVIRONMENT = resolveEnvironment();
const IS_LOCAL = ENVIRONMENT === 'local';

// Fail closed for deployable tiers: a missing value throws (naming the key, never
// the value). Local callers don't use this — they fall back to localhost.
const requireEnv = (value, key) => {
    if (!value || value.trim() === '') {
        throw new Error(
            `Missing required environment variable: ${key} ` +
            `(required for REACT_APP_ENVIRONMENT=${ENVIRONMENT})`
        );
    }
    return value;
};

// Localhost defaults are confined to `local`.
const LOCAL_API_URLS = {
    javaBackend: process.env.REACT_APP_JAVA_BACKEND_URL || 'http://localhost:8080',
    userService: process.env.REACT_APP_USER_SERVICE_URL || 'http://localhost:8080/user-service',
    nodeBackend: process.env.REACT_APP_NODE_BACKEND_URL || 'http://localhost:3002',
    wsUrl: process.env.REACT_APP_WS_URL || 'ws://localhost:8080',
};

// Deployable tiers resolve every endpoint from env — no localhost fallbacks and
// no hardcoded hostnames, so an omitted var fails the build/load loudly.
const buildDeployApiUrls = () => ({
    javaBackend: requireEnv(process.env.REACT_APP_JAVA_BACKEND_URL, 'REACT_APP_JAVA_BACKEND_URL'),
    userService: requireEnv(process.env.REACT_APP_USER_SERVICE_URL, 'REACT_APP_USER_SERVICE_URL'),
    nodeBackend: requireEnv(process.env.REACT_APP_NODE_BACKEND_URL, 'REACT_APP_NODE_BACKEND_URL'),
    wsUrl: requireEnv(process.env.REACT_APP_WS_URL, 'REACT_APP_WS_URL'),
});

const apiUrls = IS_LOCAL ? LOCAL_API_URLS : buildDeployApiUrls();

// Auth0 — local keeps placeholder defaults; deployable tiers fail closed. Do not
// weaken these requirements for non-local environments.
const auth0Domain = IS_LOCAL
    ? (process.env.REACT_APP_AUTH0_DOMAIN || 'localhost')
    : requireEnv(process.env.REACT_APP_AUTH0_DOMAIN, 'REACT_APP_AUTH0_DOMAIN');
const auth0ClientId = IS_LOCAL
    ? (process.env.REACT_APP_AUTH0_CLIENT_ID || 'local-client-id')
    : requireEnv(process.env.REACT_APP_AUTH0_CLIENT_ID, 'REACT_APP_AUTH0_CLIENT_ID');
const auth0Audience = IS_LOCAL
    ? (process.env.REACT_APP_AUTH0_AUDIENCE || 'https://localhost/')
    : requireEnv(process.env.REACT_APP_AUTH0_AUDIENCE, 'REACT_APP_AUTH0_AUDIENCE');

const config = {
    environment: ENVIRONMENT,
    // No `|| local` fallback: ENVIRONMENT is already validated against the
    // canonical set, so an unknown env has thrown above rather than resolving
    // to localhost here.
    apiUrls,
    auth0: {
        domain: auth0Domain,
        clientId: auth0ClientId,
        callbackPath: process.env.REACT_APP_AUTH0_CALLBACK_PATH || '/auth/callback',
        audience: auth0Audience
    }
};

export { SUPPORTED_ENVIRONMENTS };

export default config;

export const getApiUrl = (service) => {
    const url = config.apiUrls[service];
    if (!url) {
        throw new Error(`No URL configured for service: ${service}`);
    }
    return url;
};

// Returns the configured WebSocket base URL (REACT_APP_WS_URL). It is always
// set: localhost for `local`, and a required env var for deployable tiers (the
// config load throws above if it is missing), so there is no hardcoded fallback
// hostname that could mask a misconfiguration.
export const getWebSocketUrl = () => config.apiUrls.wsUrl;

// Helper function to get full callback URL for Auth0
export const getCallbackUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}${config.auth0.callbackPath}`;
};

// Helper function to check if we're in development
export const isDevelopment = () => {
    return ENVIRONMENT === 'local' || ENVIRONMENT === 'dev';
};

// Helper function to get the base backend URL
export const getBackendBaseUrl = () => {
    return getApiUrl('javaBackend');
};

// API endpoint helpers - Current and Future endpoints
export const API_ENDPOINTS = {
    // Trading (Current)
    SUBMIT_ORDER: `${getApiUrl('javaBackend')}/api/trade`,
    GET_TRADE_HISTORY: `${getApiUrl('javaBackend')}/api/trade-history`,

    // Trading (Future)
    GET_ORDERS: `${getApiUrl('javaBackend')}/api/orders`,
    CANCEL_ORDER: (orderId) => `${getApiUrl('javaBackend')}/api/orders/${orderId}`,
    GET_TRADES: `${getApiUrl('javaBackend')}/api/v1/trades`,
    GET_USER_TRADES: `${getApiUrl('javaBackend')}/api/v1/users/trades`,

    // Market Data (Future)
    GET_ORDER_BOOK: (symbol) => `${getApiUrl('javaBackend')}/api/v1/orderbook/${symbol}`,
    GET_MARKET_DATA: `${getApiUrl('javaBackend')}/api/v1/market-data`,

    // User (Current)
    GET_USER_PROFILE: `${getApiUrl('javaBackend')}/api/user/profile`,
    UPDATE_USER_PROFILE: `${getApiUrl('javaBackend')}/api/user/profile`,

    // Positions (Future)
    GET_POSITIONS: `${getApiUrl('javaBackend')}/api/v1/positions`,

    // Health & Test endpoints (Current)
    HEALTH_CHECK: `${getApiUrl('javaBackend')}/api/health`,
    TEST_ENDPOINT: `${getApiUrl('javaBackend')}/api/test`,

    // Actuator endpoints (Current)
    ACTUATOR_HEALTH: `${getApiUrl('javaBackend')}/api/actuator/health`,
    ACTUATOR_INFO: `${getApiUrl('javaBackend')}/api/actuator/info`,

    // WebSocket endpoints (Current)
    WS_TRADE_UPDATES: `${getWebSocketUrl()}/trade-updates`,
    WS_TRADES: `${getWebSocketUrl()}/ws/trades`,

    // WebSocket endpoints (Future)
    WS_ORDERS: `${getWebSocketUrl()}/ws/orders`,
    WS_MARKET_DATA: `${getWebSocketUrl()}/ws/market-data`,

    // Account endpoints
    ACCOUNT_STATUS: `${getApiUrl('javaBackend')}/api/v1/account/status`,
    ACCOUNT_CLOSE: `${getApiUrl('javaBackend')}/api/v1/account/close`,
    ACCOUNT_CLOSURE_PRECONDITIONS: `${getApiUrl('javaBackend')}/api/v1/account/closure-preconditions`,

    // Plaid endpoints (proxied through Java backend)
    PLAID_LINK_TOKEN: `${getApiUrl('javaBackend')}/api/plaid/link-token`,
    PLAID_EXCHANGE_TOKEN: `${getApiUrl('javaBackend')}/api/plaid/exchange-token`,
    PLAID_ITEMS: `${getApiUrl('javaBackend')}/api/plaid/items`,
    PLAID_ACCOUNTS: `${getApiUrl('javaBackend')}/api/plaid/accounts`,
    PLAID_BALANCE: `${getApiUrl('javaBackend')}/api/plaid/balance`,
    PLAID_IS_CONNECTED: `${getApiUrl('javaBackend')}/api/plaid/is-connected`,
    PLAID_DISCONNECT: `${getApiUrl('javaBackend')}/api/plaid/disconnect`,
    PLAID_DEPOSIT: `${getApiUrl('javaBackend')}/api/plaid/deposit`,
    PLAID_WITHDRAW: `${getApiUrl('javaBackend')}/api/plaid/withdraw`,
    PLAID_WITHDRAW_CONFIRM: `${getApiUrl('javaBackend')}/api/plaid/withdraw/confirm`,
    PLAID_TRANSACTIONS: `${getApiUrl('javaBackend')}/api/plaid/transactions`,
    PLAID_HEALTH: `${getApiUrl('javaBackend')}/api/plaid/health`,
};
